<script setup lang="ts">
import { useAppStore } from '@/store/app/app'
import { useCityStore } from '@/store/city/city'

import { HttpStatusCode, NotFoundError, ServerError } from '@/api/results'
import { Sliders } from '@/types/api/api-v4/slider'

import AppSeoDescription from '@/components/App/AppSeoDescription.vue'
import LandingProductList from '@/components/Main/Landing/LandingProductList.vue'
import LandingRunningLine from '@/components/Main/Landing/LandingRunningLine.vue'
import LandingSalesSlider from '@/components/Main/Landing/LandingSalesSlider.vue'
import MainAppFactoid from '@/components/Main/MainAppFactoid.vue'

type Props = {
  sliders?: Sliders | null
  seoContent?: string
}

const props = defineProps<Props>()

const { isDesktop, isMobileOrTablet } = useResponsive()

const { data: categories } = await useAsyncData(async (nuxtApp) => {
  const result = await nuxtApp?.$apiService.category.fetchMenu()

  if (result instanceof NotFoundError) {
    throw createError({
      statusCode: HttpStatusCode.NOT_FOUND,
    })
  }

  if (result instanceof ServerError) {
    throw createError({
      statusCode: HttpStatusCode.INTERNAL_SERVER_ERROR,
    })
  }

  return result?.data
})

const { cityInfo } = useCityStore()
const appStore = useAppStore()

const isCategorySticky = ref(false)
const observer = ref<IntersectionObserver | null>(null)
const RUSSIA_COUNTRY_NAME = 'Россия'

const salesSliderTitle = computed(() => {
  return props?.sliders?.saleSlider[0]?.name || ''
})

const salesSliderProducts = computed(() => {
  return (
    props?.sliders?.saleSlider.map(({ products }) => products)?.flat() || []
  )
})

const classesOfCategories = computed(() => {
  const classes = ['page__categories']

  if (isMobileOrTablet.value) {
    classes.push('page__categories--sticky')
  }

  if (isCategorySticky.value && isMobileOrTablet.value) {
    classes.push('page__categories--with-border')
  }

  return classes
})

const cityTitle = computed(() => {
  return cityInfo?.padeg?.pred || 'вашем городе'
})

const checkCountry = computed(() => {
  return cityInfo?.country?.name === RUSSIA_COUNTRY_NAME
})

const setIntersectionForCategory = () => {
  useIntersectionObserver('.landing-slider', {}, ([entry]) => {
    isCategorySticky.value = entry.intersectionRect.width === 0
  })
}

const setIntersectionForCategoryName = (categories: NodeListOf<Element>) => {
  observer.value = new IntersectionObserver(
    (entries) => {
      if (appStore.isCategoryBlockScrolling) return

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const slug = (entry.target as HTMLElement)?.dataset.categorySlug || ''

          appStore.setCategory(slug)
        }
      })
    },
    {
      threshold: 1,
    },
  )

  categories.forEach((category) => observer.value?.observe(category))
}

onMounted(() => {
  const categories = document.querySelectorAll('.landing-products__target')

  setIntersectionForCategory()
  setIntersectionForCategoryName(categories)
})

onUnmounted(() => {
  observer.value?.disconnect()

  appStore.clearCategory()
})
</script>

<template>
  <div class="page">
    <landing-running-line
      v-if="!isDesktop"
      class="page__line"
      :title="salesSliderTitle"
    />

    <div class="page__slider">
      <div v-if="isDesktop" class="page__slider-title">Скидки</div>

      <landing-sales-slider
        v-if="sliders?.saleSlider"
        :sales-slider-products="salesSliderProducts"
        :sale-slider="sliders?.saleSlider"
        :city-title="cityTitle"
      />
    </div>

    <div :class="classesOfCategories" class="page__categories">
      <main-category-list is-category-page is-landing-page />
    </div>

    <landing-product-list
      v-if="categories"
      class="page__products"
      :categories="categories"
    />

    <app-seo-description
      v-if="seoContent"
      :seo-content="seoContent"
      class="page__seo"
    />

    <client-only>
      <main-app-factoid v-if="checkCountry" class="page__app" />
    </client-only>
  </div>
</template>

<style lang="scss" scoped>
.page {
  &__line {
    margin: 12px -20px 20px;
  }

  &__slider {
    &-title {
      margin-bottom: 20px;
      font-size: 36px;
      font-weight: 900;
    }

    @include media('>desktop-mini') {
      margin-bottom: 60px;
    }
  }

  &__categories {
    z-index: 6;
    background: var(--ui-white);
    border-bottom: none;
    transition: top 0.3s ease-in;
    will-change: top;

    &--with-border {
      border-bottom: 1px solid var(--ui-grey);
    }

    &--sticky {
      position: sticky;
      top: 0;
      margin: 15px -15px;
    }

    :deep(.category__list--page) {
      border-bottom: none;
    }

    @include media('>desktop-mini') {
      margin-bottom: 60px;
    }
  }

  &__products {
    margin-bottom: 100px;
  }

  &__seo {
    margin-bottom: 100px;

    @include media('<desktop-mini') {
      margin-bottom: 120px;
    }
  }

  &__app {
    margin-bottom: 100px;

    @include media('<desktop-mini') {
      margin-bottom: 40px;
    }
  }
}
</style>
